import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../data/content";
import { scrollToId } from "../lib/smoothScroll";

const EASE = [0.22, 1, 0.36, 1];

const LineReveal = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const BADGES = ["Oltre 350+ Eventi Allestiti"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.hero}
          alt="Allestimento luminoso serale per evento"
          className="w-full h-[125%] object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#181410] via-black/40 to-black/30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(212,163,89,0.18),rgba(24,20,16,0))]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="text-xs font-mono uppercase tracking-[0.3em] text-amber-400/90 mb-7"
        >
          Noleggio Attrezzature & Installazioni su Misura
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="text-4xl sm:text-5xl lg:text-6xl font-serif font-normal tracking-tight leading-[1.08] text-[#FBF9F5] max-w-3xl"
        >
          <LineReveal delay={0.35}>Diamo luce e anima</LineReveal>
          <LineReveal delay={0.5}>
            ai tuoi momenti <em className="text-amber-300 not-italic font-serif italic">più rari.</em>
          </LineReveal>
        </h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-7 max-w-xl text-lg sm:text-xl font-light text-stone-300 leading-relaxed"
        >
          Fari a batteria wireless per architetture, luminarie vintage, cielo stellato e
          allestimenti natalizi completi. Montaggio e assistenza tecnica.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-catalog-btn"
            onClick={() => scrollToId("#catalogo")}
            className="px-7 py-3.5 rounded-full bg-amber-400 text-[#181410] text-sm font-medium hover:bg-amber-300 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_8px_30px_rgba(245,158,11,0.25)]"
          >
            Esplora il Catalogo
          </button>
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollToId("#preventivo")}
            className="px-7 py-3.5 rounded-full border border-stone-400/40 text-stone-100 text-sm font-medium hover:border-amber-400/70 hover:text-amber-300 transition-all duration-300"
          >
            Configura Preventivo
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-badges"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
        >
          {BADGES.map((b) => (
            <span key={b} className="flex items-center gap-2.5 text-xs font-mono uppercase tracking-[0.18em] text-stone-400">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_#F59E0B]" />
              {b}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-px h-14 bg-gradient-to-b from-transparent via-amber-400/60 to-transparent" />
      </motion.div>
    </section>
  );
};
